import { useQuery } from '@tanstack/react-query';
import { Column } from 'primereact/column';
import { DataTable, DataTableRowClickEvent, DataTableStateEvent } from 'primereact/datatable';
import { Skeleton } from 'primereact/skeleton';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import EmptyMyCertificatesImg from '../../../../../../../../assets/images/empty-my-certificates.svg';
import EmptyScreen from '../../../../../../../../components/empty-screen/EmptyScreen';
import { useGetPaginatedMyCertificates } from '../../../../../../../../hooks/api/certificates';
import { useMyCertificatesTableQueryParams } from '../../../../../../../../hooks/useMyCertificatesTableQueryParams';
import { Locale } from '../../../../../../../../i18n';
import { hasNoExpiryDate } from '../../../../../../../../shared/helpers/certificate-expiry';
import { getPaginatorTemplate } from '../../../../../../../../shared/helpers/get-paginator-template';
import {
  OPTIMISTIC_MY_CERTIFICATES_QUERY_KEY,
  OptimisticMyCertificate,
} from '../../../../../../../../shared/helpers/optimistic-my-certificates';
import { parseDateWithoutTime } from '../../../../../../../../shared/helpers/parse-date';
import { IMyCertificatesTableRow } from '../../../../../../../../shared/interfaces/ICertificate';
import CertificateStatusTag from '../../../../../certificate-status-tag/CertificateStatusTag';
import AddCertificateButton from '../add-certificate-button/AddCertificateButton';
import ManageSitesButton from '../manage-sites-button/ManageSitesButton';
import styles from './MyCertificatesTable.module.css';

// A pending row is a certificate whose upload is still in flight: no id yet, and no validity or
// status because the server decides those — the table skeletons them.
type MyCertificateRow = Omit<IMyCertificatesTableRow, 'id'> & {
  id?: number;
  rowKey: string;
  isPending?: boolean;
};

const MyCertificatesTable = (): JSX.Element => {
  const { skip, size, sort, setParams } = useMyCertificatesTableQueryParams();
  const [sortField, sortOrder] = sort ? sort.split('-') : [undefined, undefined];
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const {
    i18n: { language },
    t,
  } = useTranslation();

  const navigate = useNavigate();

  const { data, isLoading } = useGetPaginatedMyCertificates({
    skip,
    size,
    sort,
  });

  useEffect(() => {
    if (data) {
      setTotalRecords(data?.pagination?.total || 0);
    }
  }, [data]);

  // Certificates whose upload is still running live in a dedicated cache entry (see
  // optimistic-my-certificates.ts) so the ['certificate'] invalidation never wipes them. Reading
  // them through useQuery is what makes the table re-render on setQueryData.
  const { data: optimisticCertificates = [] } = useQuery({
    queryKey: OPTIMISTIC_MY_CERTIFICATES_QUERY_KEY,
    queryFn: (): OptimisticMyCertificate[] => {
      return [];
    },
    enabled: false,
    initialData: [],
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  // Pending rows are pinned above the list on every page and sort. They are an in-progress
  // indicator rather than a claim about sort position — the list is ordered by expiry date, which
  // a certificate that does not exist yet has no place in.
  const tableRows = useMemo<MyCertificateRow[]>(() => {
    const realRows = (data?.data ?? []).map((row) => {
      return { ...row, rowKey: String(row.id) };
    });

    const pendingRows = optimisticCertificates.map((row) => {
      return { ...row, rowKey: `optimistic-${row.optimisticKey}`, isPending: true };
    });

    return [...pendingRows, ...realRows];
  }, [data, optimisticCertificates]);

  // A pending certificate is still uploading, so its server-decided columns show a skeleton.
  const pendingCellSkeleton = <Skeleton height='1rem' width='80%' />;

  const handleRowClick = (event: DataTableRowClickEvent) => {
    const target = event.originalEvent.target as HTMLElement | null;
    const rowData = event.data as MyCertificateRow;

    // A pending row has no id yet, so there is nothing to navigate to.
    if (!rowData.id || target?.closest('button, a, [role="button"]')) {
      return;
    }

    navigate(`/certificates/my-certificates/${rowData.id}/details`);
  };

  const validUntilTemplate = (rowData: MyCertificateRow): React.ReactNode => {
    if (rowData.isPending) {
      return pendingCellSkeleton;
    }

    if (hasNoExpiryDate(rowData.validUntil)) {
      return (
        <span className='font-italic text-gray-700'>{t('addCertificateDialog.noExpiration')}</span>
      );
    }

    return parseDateWithoutTime(rowData.validUntil, language as Locale);
  };

  const statusTemplate = (rowData: MyCertificateRow) => {
    if (rowData.isPending) {
      return pendingCellSkeleton;
    }

    return <CertificateStatusTag status={rowData.status} />;
  };

  const enclosedSitesTemplate = (rowData: MyCertificateRow): React.ReactNode => {
    if (rowData.appliesToAllSites) {
      return t('addCertificateDialog.inputs.companyWide');
    }

    if (!rowData.enclosedSites?.length) {
      return '-';
    }

    const [firstSite, ...remainingSites] = rowData.enclosedSites;

    if (!remainingSites.length) {
      return firstSite.name;
    }

    return `${firstSite.name} ${t(
      'certificatesPage.tabs.myCertificatesTab.myCertificatesTableColumns.moreCount',
      { count: remainingSites.length },
    )}`;
  };

  // A pending row still needs a table to live in, so the very first certificate replaces the empty
  // screen as soon as its upload starts rather than when it finishes.
  const isTableEmpty = !isLoading && tableRows.length === 0;

  if (isTableEmpty) {
    return (
      <EmptyScreen imageSrc={EmptyMyCertificatesImg}>
        <div className='flex flex-column align-items-center p-4 gap-3'>
          <h4 className='m-0 p-0'>
            {t('certificatesPage.tabs.myCertificatesTab.emptyScreen.title')}
          </h4>
          <span>{t('certificatesPage.tabs.myCertificatesTab.emptyScreen.text')}</span>
          <AddCertificateButton />
        </div>
      </EmptyScreen>
    );
  }

  return (
    <div className={styles['my-certificates-table']}>
      <DataTable
        value={tableRows}
        dataKey='rowKey'
        loading={isLoading}
        header={
          <div className='flex flex-column md:flex-row gap-2 w-full'>
            <AddCertificateButton />
            <ManageSitesButton />
          </div>
        }
        lazy
        rows={size}
        paginatorPosition='top'
        paginator
        sortField={sortField}
        sortOrder={sortField ? (sortOrder === 'desc' ? -1 : 1) : undefined}
        rowClassName={(rowData: MyCertificateRow) => {
          return rowData.id ? 'cursor-pointer my-hoverable-row' : '';
        }}
        onRowClick={handleRowClick}
        onSort={(event: DataTableStateEvent) => {
          const newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
          setParams({
            sort: `${event.sortField}-${newSortOrder}`,
            page: 1,
          });
        }}
        scrollable
        paginatorTemplate={getPaginatorTemplate({
          totalRecords,
          rowsPerPage: size,
          skip,
          onNext: () => {
            setParams({ page: (skip + size) / size + 1 });
          },
          onPrev: () => {
            setParams({ page: (skip + size) / size - 1 });
          },
        })}
        tableStyle={{
          tableLayout: 'fixed',
          minWidth: '800px',
        }}
        emptyMessage={t('global.emptyTableMessage')}
      >
        <Column
          field='type'
          header={t(
            'certificatesPage.tabs.myCertificatesTab.myCertificatesTableColumns.certificateType',
          )}
          sortable
          style={{ width: '30%', wordBreak: 'break-word' }}
        />

        <Column
          field='enclosedSites'
          header={t(
            'certificatesPage.tabs.myCertificatesTab.myCertificatesTableColumns.enclosedSites',
          )}
          body={enclosedSitesTemplate}
          style={{ width: '40%', wordBreak: 'break-word' }}
        />

        <Column
          header={t(
            'certificatesPage.tabs.myCertificatesTab.myCertificatesTableColumns.validUntil',
          )}
          body={validUntilTemplate}
          sortable
          sortField='validUntil'
          style={{ width: '15%' }}
        />

        <Column
          header={t('certificatesPage.tabs.myCertificatesTab.myCertificatesTableColumns.status')}
          body={statusTemplate}
          sortable
          sortField='status'
          style={{ width: '15%' }}
        />
      </DataTable>
    </div>
  );
};

export default MyCertificatesTable;
